/**
 * Input — text input primitive.
 *
 * Design system mapping (mch-input):
 *   • Height:    32px (default) / 40px (lg) / 24px (sm)
 *   • Border:    border webex-gray-500 / dark: webex-gray-400
 *   • Radius:    rounded-md (8px)
 *   • Focus:     ring-2 webex-blue-600 ring-offset-1
 *   • Error:     border webex-red-600 / dark: webex-red-500
 *   • Disabled:  bg webex-gray-100 / dark: webex-gray-900 + opacity-70
 *
 * Supports leadingIcon/trailingIcon slots (positioned absolutely).
 */
import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@lib/cn"

export const inputVariants = cva(
  [
    "ui-control",
    "block w-full rounded-md bg-white dark:bg-webex-gray-950",
    "text-sm text-webex-gray-1000 dark:text-white",
    "placeholder:text-webex-gray-500 dark:placeholder:text-webex-gray-400",
    "border transition-colors duration-150 motion-reduce:transition-none",
    "focus:outline-none focus:ring-2 focus:ring-webex-blue-600 focus:ring-offset-1 focus:ring-offset-white dark:focus:ring-offset-webex-gray-950",
    "disabled:cursor-not-allowed disabled:opacity-70 disabled:bg-webex-gray-100 dark:disabled:bg-webex-gray-900",
  ].join(" "),
  {
    variants: {
      state: {
        default: "border-webex-gray-500 dark:border-webex-gray-400",
        error:   "border-webex-red-600 dark:border-webex-red-500 focus:ring-webex-red-600",
      },
      size: {
        sm:      "h-6 px-2 text-xs",
        default: "h-8 px-3",
        lg:      "h-10 px-4 text-base",
      },
    },
    defaultVariants: {
      state: "default",
      size: "default",
    },
  },
)

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof inputVariants> {
  leadingIcon?: React.ReactNode
  trailingIcon?: React.ReactNode
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, state, size, leadingIcon, trailingIcon, type = "text", ...props }, ref) => {
    if (leadingIcon || trailingIcon) {
      // Wrapper owns width / margin (via className passthrough) so absolutely-
      // positioned icon slots anchor to the visible input edge, not the row edge.
      return (
        <span className={cn("relative inline-block align-middle w-full", className)}>
          {leadingIcon && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 left-2 flex items-center text-webex-gray-500 dark:text-webex-gray-400 [&>*]:size-4"
            >
              {leadingIcon}
            </span>
          )}
          <input
            ref={ref}
            type={type}
            className={cn(
              inputVariants({ state, size }),
              leadingIcon && "pl-8",
              trailingIcon && "pr-8",
            )}
            {...props}
          />
          {trailingIcon && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-webex-gray-500 dark:text-webex-gray-400 [&>*]:size-4"
            >
              {trailingIcon}
            </span>
          )}
        </span>
      )
    }
    return (
      <input
        ref={ref}
        type={type}
        className={cn(inputVariants({ state, size }), className)}
        {...props}
      />
    )
  },
)
Input.displayName = "Input"
